import { useEffect, useRef, useState } from "react";
import maplibregl from "maplibre-gl";

import type { FeatureCollection } from "geojson";
import { MAP_COLORS } from "@/lib/map-colors";
import { BASEMAP_STYLE } from "@/lib/mapbox";
import { apiBaseURL } from "@/initializers/axios-client";

const CHECKERED_FLAG_SVG = `
<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
  <circle cx="12" cy="12" r="10" fill="white" stroke="black" stroke-width="2"/>
  <path d="M6 6h2v2H6zm2 2h2v2H8zm2-2h2v2h-2zm2 2h2v2h-2zm2-2h2v2h-2zm2 2h2v2h-2zM6 10h2v2H6zm2 2h2v2H8zm2-2h2v2h-2zm2 2h2v2h-2zm2-2h2v2h-2zm2 2h2v2h-2zM6 14h2v2H6zm2 2h2v2H8zm2-2h2v2h-2zm2 2h2v2h-2zm2-2h2v2h-2zm2 2h2v2h-2z" fill="black"/>
</svg>`;

// An up-pointing solid chevron used as a direction arrow. Placed at fixed points
// along the route and rotated per-point by the travel bearing (so 0deg = north).
// Added as an SDF image (alpha mask only), so it is tinted at render via
// icon-color.
const ROUTE_ARROW_SVG = `
<svg width="16" height="16" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
  <path d="M8 2 L14 12 L8 9.5 L2 12 Z" fill="black"/>
</svg>`;

interface UseMapInitializationProps {
  center: [number, number];
  streetsTilesUrl: string;
  matchedStreetsGeoJSON: FeatureCollection;
  stravaRunsGeoJSON: FeatureCollection;
  onDragStart: () => void;
  onDrag: () => void;
  onDragEnd: () => void;
  onLoad?: () => void;
}

export const useMapInitialization = ({
  center,
  streetsTilesUrl,
  matchedStreetsGeoJSON,
  stravaRunsGeoJSON,
  onDragStart,
  onDrag,
  onDragEnd,
  onLoad,
}: UseMapInitializationProps) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<maplibregl.Map | null>(null);
  // The layers control is a native-styled slot in the control stack; React
  // portals the actual menu (trigger + popover) into this node so it can use
  // the app's shadcn components. See MapView. The tools slot is the same idea,
  // one notch lower, for the admin street tools.
  const [layersSlot, setLayersSlot] = useState<HTMLDivElement | null>(null);
  const [toolsSlot, setToolsSlot] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!mapContainer.current) return;

    map.current = new maplibregl.Map({
      container: mapContainer.current,
      style: BASEMAP_STYLE,
      center,
      zoom: 12,
      // The street vector tiles are served by our own API behind bearer auth;
      // attach the token to those requests (and only those). Basemap and other
      // hosts are left untouched.
      transformRequest: (url) => {
        if (url.startsWith(`${apiBaseURL}/`)) {
          const token = localStorage.getItem("access_token");
          if (token) {
            return { url, headers: { Authorization: `Bearer ${token}` } };
          }
        }
        return { url };
      },
    });

    // Native controls, bottom-left, stacked top-to-bottom: locate me, zoom +/-
    // & compass, layers. MapLibre prepends bottom-corner controls, so add them
    // bottom-to-top for the topmost control to end up on top.
    const geolocate = new maplibregl.GeolocateControl({
      positionOptions: { enableHighAccuracy: true },
      // Cap the zoom so locating only nudges in from the city view rather than
      // jumping to street level (the default maxZoom is 15).
      fitBoundsOptions: { maxZoom: 12.5 },
      trackUserLocation: true,
    });

    // Very bottom of the stack: a native-styled slot React fills with the admin
    // street tools via a portal. Added before layers so it stacks below it.
    const toolsContainer = document.createElement("div");
    toolsContainer.className = "maplibregl-ctrl maplibregl-ctrl-group";
    const toolsControl: maplibregl.IControl = {
      onAdd: () => toolsContainer,
      onRemove: () => toolsContainer.parentNode?.removeChild(toolsContainer),
    };
    map.current.addControl(toolsControl, "bottom-left");
    setToolsSlot(toolsContainer);

    // Above the tools: an empty native-styled slot that React fills with the
    // layers menu via a portal.
    const layersContainer = document.createElement("div");
    layersContainer.className = "maplibregl-ctrl maplibregl-ctrl-group";
    const layersControl: maplibregl.IControl = {
      onAdd: () => layersContainer,
      onRemove: () => layersContainer.parentNode?.removeChild(layersContainer),
    };
    map.current.addControl(layersControl, "bottom-left");
    setLayersSlot(layersContainer);

    // Middle of the stack.
    map.current.addControl(
      new maplibregl.NavigationControl({
        showZoom: true,
        showCompass: true, // rotates with bearing; click resets north
        visualizePitch: false, // compass only rotates, doesn't tilt with pitch
      }),
      "bottom-left",
    );

    // Top of the stack.
    map.current.addControl(geolocate, "bottom-left");

    map.current.on("dragstart", onDragStart);
    map.current.on("drag", onDrag);
    map.current.on("dragend", onDragEnd);

    // Keep the GL canvas filling its container when the sidebar collapses/expands.
    // The sidebar width snaps instantly (no transition), so this fires once and
    // the map resizes cleanly in a single step.
    const resizeObserver = new ResizeObserver(() => map.current?.resize());
    resizeObserver.observe(mapContainer.current);

    map.current.on("load", () => {
      if (!map.current) return;

      const flagImg = new Image();
      flagImg.onload = () => {
        if (!map.current) return;
        map.current.addImage("checkered-flag", flagImg);
      };
      flagImg.src =
        "data:image/svg+xml;charset=utf-8," +
        encodeURIComponent(CHECKERED_FLAG_SVG);

      // SDF so icon-color can tint it white over the orange route line.
      const arrowImg = new Image();
      arrowImg.onload = () => {
        if (!map.current) return;
        map.current.addImage("route-arrow", arrowImg, { sdf: true });
      };
      arrowImg.src =
        "data:image/svg+xml;charset=utf-8," +
        encodeURIComponent(ROUTE_ARROW_SVG);

      // Base streets come from server-side vector tiles (PostGIS ST_AsMVT) so
      // only the on-screen tiles load - a big city stays smooth where the full
      // GeoJSON did not. Auth rides along via transformRequest below.
      map.current.addSource("streets", {
        type: "vector",
        tiles: [streetsTilesUrl],
        minzoom: 0,
        maxzoom: 16,
      });

      map.current.addSource("matched-streets", {
        type: "geojson",
        data: matchedStreetsGeoJSON,
        tolerance: 0.375,
        buffer: 0,
        maxzoom: 16,
      });

      map.current.addSource("strava-runs", {
        type: "geojson",
        data: stravaRunsGeoJSON,
        tolerance: 0.375,
        buffer: 0,
        maxzoom: 16,
      });

      map.current.addSource("waypoints", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });

      map.current.addSource("route", {
        type: "geojson",
        data: {
          type: "Feature",
          properties: {},
          geometry: {
            type: "LineString",
            coordinates: [],
          },
        },
      });

      map.current.addSource("suggested-routes", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });

      map.current.addSource("start-point", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });

      map.current.addSource("end-point", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });

      // Moving head dot for the picked-route reveal. The reveal grows the real
      // orange `route` line itself (via use-route-animation), so only the dot
      // needs its own source.
      map.current.addSource("route-animation-point", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });

      // Direction arrows as fixed geographic points (each carrying a `bearing`),
      // so they stay put across zooms instead of re-placing like line symbols.
      map.current.addSource("route-arrows", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });

      map.current.addLayer({
        id: "streets",
        type: "line",
        source: "streets",
        "source-layer": "streets",
        layout: {
          "line-join": "round",
          "line-cap": "round",
        },
        paint: {
          "line-color": MAP_COLORS.street,
          "line-width": 1,
        },
      });

      // Optional overlay of raw Strava run tracks, hidden until toggled on.
      // Added before matched-streets so the green coverage renders over it.
      map.current.addLayer({
        id: "strava-runs",
        type: "line",
        source: "strava-runs",
        layout: {
          "line-join": "round",
          "line-cap": "round",
          visibility: "none",
        },
        paint: {
          "line-color": MAP_COLORS.stravaRun,
          "line-width": [
            "interpolate",
            ["linear"],
            ["zoom"],
            10,
            1,
            13,
            2,
            16,
            3,
          ],
          "line-opacity": 0.8,
        },
      });

      map.current.addLayer({
        id: "matched-streets",
        type: "line",
        source: "matched-streets",
        minzoom: 10, // Render matched streets earlier for overview
        layout: {
          "line-join": "round",
          "line-cap": "round",
        },
        paint: {
          "line-color": MAP_COLORS.covered,
          "line-width": [
            "interpolate",
            ["linear"],
            ["zoom"],
            10,
            1,
            13,
            2,
            16,
            4,
          ],
        },
      });

      map.current.addLayer({
        id: "waypoints",
        type: "circle",
        source: "waypoints",
        filter: ["==", ["get", "pointType"], "start"],
        paint: {
          "circle-radius": 8,
          "circle-color": MAP_COLORS.routeStart,
          "circle-stroke-width": 2,
          "circle-stroke-color": MAP_COLORS.pointStroke,
        },
      });

      map.current.addLayer({
        id: "endpoint",
        type: "symbol",
        source: "waypoints",
        filter: ["==", ["get", "pointType"], "end"],
        layout: {
          "icon-image": "checkered-flag",
          "icon-size": 1,
          "icon-allow-overlap": true,
        },
      });

      map.current.addLayer({
        id: "intermediate-points",
        type: "circle",
        source: "waypoints",
        filter: ["==", ["get", "pointType"], "waypoint"],
        paint: {
          "circle-radius": 6,
          "circle-color": MAP_COLORS.waypoint,
          "circle-stroke-width": 2,
          "circle-stroke-color": MAP_COLORS.pointStroke,
        },
      });

      // Suggested-route candidates, drawn under the picked (orange) route so the
      // selected one reads on top once loaded into the `route` source.
      map.current.addLayer({
        id: "suggested-routes",
        type: "line",
        source: "suggested-routes",
        layout: {
          "line-join": "round",
          "line-cap": "round",
        },
        paint: {
          "line-color": MAP_COLORS.suggestedRoute,
          "line-width": 4,
          "line-opacity": [
            "case",
            ["boolean", ["feature-state", "hover"], false],
            0.9,
            0.55,
          ],
        },
      });

      map.current.addLayer({
        id: "route",
        type: "line",
        source: "route",
        layout: {
          "line-join": "round",
          "line-cap": "round",
        },
        paint: {
          "line-color": MAP_COLORS.routeLine,
          "line-width": 5,
          "line-opacity": 0.75,
        },
      });

      // Direction arrows at fixed points along the route (see the route-arrows
      // effect in MapView). Each point carries a `bearing`; the up-pointing icon
      // is rotated to it and aligned to the map, so arrows hold their spot and
      // orientation across zooms.
      map.current.addLayer({
        id: "route-arrows",
        type: "symbol",
        source: "route-arrows",
        layout: {
          "icon-image": "route-arrow",
          "icon-size": 1.4,
          "icon-rotate": ["get", "bearing"],
          "icon-rotation-alignment": "map",
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
        },
        paint: {
          "icon-color": MAP_COLORS.routeArrow,
          "icon-opacity": 1,
        },
      });

      // Head dot leading the reveal, drawn above the route.
      map.current.addLayer({
        id: "route-animation-point",
        type: "circle",
        source: "route-animation-point",
        paint: {
          "circle-radius": 6,
          "circle-color": MAP_COLORS.routeAnimation,
          "circle-stroke-width": 2,
          "circle-stroke-color": MAP_COLORS.pointStroke,
        },
      });

      map.current.addLayer({
        id: "start-point",
        type: "circle",
        source: "start-point",
        paint: {
          "circle-radius": 7,
          "circle-color": MAP_COLORS.suggestedStart,
          "circle-stroke-width": 2,
          "circle-stroke-color": MAP_COLORS.pointStroke,
        },
      });

      // End point renders as a checkered flag inside a circle, matching the
      // manual-route finish marker (the same `checkered-flag` image).
      map.current.addLayer({
        id: "end-point",
        type: "symbol",
        source: "end-point",
        layout: {
          "icon-image": "checkered-flag",
          // 0.7 renders the 24px flag at ~17px, close to the start dot's size.
          "icon-size": 0.7,
          "icon-allow-overlap": true,
        },
      });

      // Signal that the style + base layers are ready, so consumers only touch
      // the map (add sources/layers) once the style is ready.
      onLoad?.();
    });

    return () => {
      resizeObserver.disconnect();
      setLayersSlot(null);
      setToolsSlot(null);
      map.current?.remove();
    };
  }, []);

  return { mapContainer, map, layersSlot, toolsSlot };
};
